import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  ShieldCheck,
  Smartphone,
  Loader2,
  AlertCircle,
  CheckCircle2,
  LayoutDashboard,
  Settings,
} from 'lucide-react';
import { authService } from '../api/auth';
import { useAuth } from '../context/AuthContext';
import { otpService } from '../api/otp';
import { firebaseOTP } from '../api/firebaseOTP';
import { AdminBrandLogo, useStoreBrand } from '../admin/components/AdminBrandLogo';

const ADMIN_BENEFITS = [
  {
    icon: LayoutDashboard,
    title: 'Store dashboard',
    desc: 'Orders, revenue, inventory, and reports in one place.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure access',
    desc: 'Authorized administrators only — activity is monitored.',
  },
  {
    icon: Settings,
    title: 'Full control',
    desc: 'Products, homepage, payments, shipping, and settings.',
  },
] as const;

const inputBase =
  'w-full text-sm text-slate-900 bg-white border border-slate-200 rounded-lg shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors disabled:bg-slate-50 disabled:text-slate-500';

type LoginMode = 'email' | 'mobile';

const AdminLogin = () => {
  const [mode, setMode] = useState<LoginMode>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { brand: storeBrand, logoFailed, setLogoFailed } = useStoreBrand();

  useEffect(() => {
    const admin = authService.getCurrentAdmin();
    if (admin && admin.role === 'admin' && authService.getAdminToken()) {
      navigate('/system-control-panel-7x9k2m', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (otpTimer <= 0) return;
    const t = setInterval(() => setOtpTimer((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpTimer]);

  useEffect(() => {
    setError('');
    setSuccess('');
    setOtpSent(false);
    setOtpCode('');
    setOtpTimer(0);
  }, [mode]);

  const finishLogin = (loginResult: { token?: string; user?: { role?: string } }) => {
    if (!loginResult.token || loginResult.user?.role !== 'admin') {
      setError('Access denied: Admin privileges required.');
      return false;
    }
    setSuccess('Signed in. Opening admin panel…');
    checkAuth();
    setTimeout(() => navigate('/system-control-panel-7x9k2m', { replace: true }), 400);
    return true;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (!email.trim() || !password) {
        setError('Email and password are required.');
        return;
      }
      const loginResult = await authService.adminLogin({ email: email.trim(), password });
      finishLogin(loginResult);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Enter a valid 10-digit mobile number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const config = await otpService.fetchProviderConfig();
      if (config.provider !== 'firebase') {
        setError('Mobile login requires Firebase OTP (Ultra Settings → OTP).');
        return;
      }
      await otpService.sendFirebaseOtpWithRecovery(digits, config);
      setOtpSent(true);
      setOtpTimer(60);
      setSuccess('OTP sent to your mobile.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleMobileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !otpSent) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const verified = await firebaseOTP.verifyOTP(otpCode.trim());
      if (!verified.idToken) throw new Error('OTP verification failed');
      const loginResult = await authService.adminPhoneLogin(
        `+91${phone.replace(/\D/g, '')}`,
        verified.idToken
      );
      finishLogin(loginResult);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Mobile login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-[72px] sm:pt-20">
      <aside className="fixed left-0 top-[72px] bottom-0 z-0 hidden w-[380px] flex-col bg-slate-900 text-white border-r border-slate-800 sm:top-20 lg:flex xl:w-[400px]">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950" />
        <div className="pointer-events-none absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_70%_40%_at_100%_0%,rgba(220,38,38,0.22),transparent_55%)]" />

        <div className="relative z-10 flex h-full min-h-0 flex-col">
          <div className="shrink-0 px-8 pb-6 pt-8 xl:px-10 xl:pt-9">
            <Link to="/" className="inline-flex items-center">
              <AdminBrandLogo
                brand={storeBrand}
                logoFailed={logoFailed}
                onLogoError={() => setLogoFailed(true)}
                size="lg"
                onDark
              />
            </Link>
          </div>

          <div className="flex min-h-0 flex-1 flex-col justify-center overflow-y-auto px-8 pb-8 xl:px-10">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary-300/90">
              Administrator portal
            </p>
            <h1 className="text-[1.35rem] font-bold leading-snug tracking-tight text-white xl:text-2xl">
              Manage {storeBrand.name}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              Sign in to update products, orders, and store settings securely.
            </p>

            <ul className="mt-8 space-y-6">
              {ADMIN_BENEFITS.map(({ icon: Icon, title, desc }) => (
                <li key={title} className="flex gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-white/12 bg-white/5 text-primary-300">
                    <Icon className="h-[0.95rem] w-[0.95rem]" strokeWidth={2} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-100">{title}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-slate-500">{desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="shrink-0 border-t border-white/10 px-8 py-4 text-[11px] text-slate-600 xl:px-10">
            Authorized personnel only · © {new Date().getFullYear()} {storeBrand.name}
          </p>
        </div>
      </aside>

      <div className="relative z-[1] flex min-h-[calc(100vh-5rem)] flex-col lg:pl-[380px] xl:pl-[400px]">
        <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-8 lg:px-12 lg:py-12">
          <div className="w-full max-w-[480px]">
            <div className="mb-8 text-center lg:hidden">
              <Link to="/" className="inline-flex justify-center">
                <AdminBrandLogo
                  brand={storeBrand}
                  logoFailed={logoFailed}
                  onLogoError={() => setLogoFailed(true)}
                  size="md"
                />
              </Link>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Admin sign in
              </h2>
              <p className="mt-2 text-sm text-slate-600 sm:text-base">
                Shopping on the store?{' '}
                <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
                  Customer login
                </Link>
              </p>
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-200/50 sm:p-8">
              <div className="mb-6 flex items-center gap-2 border-b border-slate-100 pb-6 text-sm text-slate-600">
                <ShieldCheck className="h-4 w-4 shrink-0 text-primary-600" />
                <span>Secure administrator access — login attempts are logged.</span>
              </div>

              <div className="mb-6 flex rounded-lg bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => setMode('email')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold transition-colors ${
                    mode === 'email' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Mail className="h-4 w-4" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setMode('mobile')}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold transition-colors ${
                    mode === 'mobile' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Smartphone className="h-4 w-4" />
                  Mobile OTP
                </button>
              </div>

              {error && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              {mode === 'email' ? (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="admin-email" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Admin email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="admin-email"
                        type="email"
                        name="email"
                        autoComplete="username"
                        placeholder="admin@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`${inputBase} py-2.5 pl-10`}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="admin-password" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        id="admin-password"
                        type={showPassword ? 'text' : 'password'}
                        name="current-password"
                        autoComplete="current-password"
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`${inputBase} py-2.5 pl-10 pr-11`}
                        required
                        disabled={loading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        disabled={loading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-500"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in…
                      </>
                    ) : (
                      <>
                        Access admin panel
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleMobileSubmit} className="space-y-4">
                  <p className="text-xs leading-relaxed text-slate-500">
                    Requires a verified admin mobile number configured under Account Settings → Security.
                  </p>
                  <div id="recaptcha-container" className="min-h-0" />

                  <div>
                    <label htmlFor="admin-phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Mobile number
                    </label>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <div className="relative flex-1">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500">
                          +91
                        </span>
                        <input
                          id="admin-phone"
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel"
                          maxLength={10}
                          value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                          className={`${inputBase} py-2.5 pl-12`}
                          placeholder="10-digit mobile"
                          required
                          disabled={loading}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={sendOtp}
                        disabled={loading || otpTimer > 0 || phone.length !== 10}
                        className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-500"
                      >
                        {loading && !otpSent ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Smartphone className="h-4 w-4" />
                        )}
                        {otpTimer > 0 ? `${otpTimer}s` : otpSent ? 'OTP sent' : 'Send OTP'}
                      </button>
                    </div>
                  </div>

                  {otpSent && (
                    <div>
                      <label htmlFor="admin-otp" className="mb-1.5 block text-sm font-medium text-slate-700">
                        6-digit OTP
                      </label>
                      <input
                        id="admin-otp"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={otpCode}
                        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className={`${inputBase} py-3 text-center font-mono text-xl tracking-[0.4em]`}
                        placeholder="• • • • • •"
                        required
                        disabled={loading}
                      />
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || !otpSent || otpCode.length !== 6}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-500"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      'Sign in with OTP'
                    )}
                  </button>
                </form>
              )}

              <div className="mt-6 border-t border-slate-100 pt-6 text-center">
                <Link
                  to="/"
                  className="text-sm font-medium text-slate-600 transition-colors hover:text-primary-600"
                >
                  ← Back to store
                </Link>
              </div>
            </div>

            <p className="mt-6 text-center text-xs text-slate-500 lg:hidden">
              Secure admin access · {storeBrand.name}
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLogin;
